import { useRef, type RefObject } from 'react'
import { ScrollTrigger, useGSAP } from '@/lib/gsap'

/**
 * Scroll progress (0..1) of an element, written into a ref by a ScrollTrigger: no
 * re-renders and no smoothing. Read it in `useFrame` and damp it yourself.
 *
 * The default window runs from the element's top hitting the viewport bottom
 * (`'top bottom'`) to its bottom leaving the viewport top (`'bottom top'`). Any
 * `ScrollTrigger.Vars` you pass override those defaults. `vars` are read once, on mount.
 *
 * Returns a React 19 `RefObject<number>`, the mutable ref formerly typed `MutableRefObject`.
 *
 * @example
 * const progress = useScrollProgress(sectionRef, { start: 'top top', end: 'bottom bottom' })
 * useFrame((_, delta) => { smoothed.current = damp(smoothed.current, progress.current, 4, delta) })
 */
export function useScrollProgress<T extends Element>(
  ref: RefObject<T | null>,
  vars?: ScrollTrigger.Vars,
): RefObject<number> {
  const progress = useRef(0)

  useGSAP(() => {
    if (!ref.current) return
    const trigger = ScrollTrigger.create({
      trigger: ref.current,
      start: 'top bottom',
      end: 'bottom top',
      ...vars,
      onUpdate: (self) => {
        progress.current = self.progress
        vars?.onUpdate?.(self)
      },
      onRefresh: (self) => {
        progress.current = self.progress
        vars?.onRefresh?.(self)
      },
    })
    progress.current = trigger.progress
  })

  return progress
}
